import { plural } from '../../../../shared/helpers/plural';
import queryClient from '../../../ReactQuery/client';
import useToastedMutation from '../../../ReactQuery/useToastedMutation';
import { remove } from '../../../redux/api';
import type {
  RemoveReleaseGroupsFromTeamRequest,
  RemoveReleaseGroupsFromTeamResponse,
} from '../../../../shared/types/routes/teams';
import type { Team } from '../../../types';

import { getTeamQueryKey } from './useTeam';

type RemoveReleaseGroupsFromTeamVariables = {
  teamId: number;
  releaseGroupIds: number[];
};

//  Actual function to remove release groups from a team
async function removeReleaseGroupsFromTeam({ teamId, releaseGroupIds }: RemoveReleaseGroupsFromTeamVariables) {
  const response = await remove<RemoveReleaseGroupsFromTeamRequest, RemoveReleaseGroupsFromTeamResponse>(`/api/teams/${teamId}/release-groups`, {
    body: {
      ids: releaseGroupIds,
      action: 'remove',
    },
    json: true,
  });
  return response.data as RemoveReleaseGroupsFromTeamResponse;
}

/**
 * Hook for removing release groups from a team.
 */
export default function useRemoveReleaseGroupsFromTeam() {
  return useToastedMutation({
    mutationFn: removeReleaseGroupsFromTeam,
    successToastMessage: (_response, variables: RemoveReleaseGroupsFromTeamVariables) =>
      `Removed ${variables.releaseGroupIds.length} ${plural(variables.releaseGroupIds.length, 'release group')} from team.`,
    errorToastMessage: 'Failed to remove release group(s) from team.',
    onSuccess: (response, variables: RemoveReleaseGroupsFromTeamVariables) => {
      // Update the team's user count in the cache
      queryClient.setQueryData(getTeamQueryKey(variables.teamId), (oldTeam?: Team) => {
        if (!oldTeam) {
          return oldTeam;
        }
        return {
          ...oldTeam,
          //  Map the release group IDs to the team release group objects, as that's what this endpoint returns
          teamReleaseGroups: response.releaseGroups.map((releaseGroupId: number) => ({ releaseGroupId })),
        };
      });

      // Also re-fetch the team to get any other changes
      void queryClient.invalidateQueries({ queryKey: getTeamQueryKey(variables.teamId) });

      // TODO: we gotta invalidate the release groups query, which is actually complicated because it's some bullshit off the org
      // queryClient.invalidateQueries({ queryKey: getReleaseGroupsQueryKey() });
    },
  });
}
